import React, { useState, useEffect } from 'react';
import { submitCounsellingRequest } from '../services/contactService';

// ---------------------------------------------------------------------------
// Inline SVG Icon Components (unchanged from original)
// ---------------------------------------------------------------------------
const UserIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const PhoneIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const MailIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const MessageIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const CheckIcon = ({ className, style }) => (
  <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const HeartIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const BookIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

// ---------------------------------------------------------------------------
// Color palette (unchanged — matches Gallery component)
// ---------------------------------------------------------------------------
const colors = {
  background:    '#F0FDF7',
  primaryDark:   '#134E4A',
  primaryLight:  '#2DD4BF',
  textPrimary:   '#134E4A',
  textSecondary: '#4A5568',
  borderLight:   '#E0F2F1',
};

// ---------------------------------------------------------------------------
// Reason options for the mentorship dropdown
// ---------------------------------------------------------------------------
const REASONS = [
  'UPSC Preparation Guidance',
  'MPSC Strategy Planning',
  'Career Path Consultation',
  'Study Plan Development',
  'Motivation & Mental Health',
  'Subject Selection Advice',
  'Mock Test Analysis',
  'Interview Preparation',
  'Other',
];

// ---------------------------------------------------------------------------
// Success State Component
// ---------------------------------------------------------------------------
const SuccessView = ({ formData, onReset }) => (
  <div className="min-h-screen pt-20" style={{ backgroundColor: colors.background }}>
    <div className="flex items-start justify-center p-4 py-12">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ backgroundColor: `${colors.primaryLight}33` }}
        >
          <CheckIcon className="w-10 h-10" style={{ color: colors.primaryDark }} />
        </div>

        <h2 className="text-2xl font-bold mb-4" style={{ color: colors.primaryDark }}>
          Request Submitted Successfully!
        </h2>
        <p className="mb-6" style={{ color: colors.textSecondary }}>
          Thank you for your interest in our counselling services. We have received your request
          and our team will contact you within 24–48 hours to schedule your session. A confirmation
          email has been sent to <strong>{formData.email}</strong>.
        </p>

        <div className="rounded-lg p-4 mb-6 text-left" style={{ backgroundColor: colors.background }}>
          <h3 className="font-semibold mb-3" style={{ color: colors.primaryDark }}>Your Details:</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4" style={{ color: colors.primaryDark }} />
              <span className="text-sm" style={{ color: colors.textPrimary }}>{formData.fullName}</span>
            </div>
            <div className="flex items-center gap-2">
              <MailIcon className="w-4 h-4" style={{ color: colors.primaryDark }} />
              <span className="text-sm" style={{ color: colors.textPrimary }}>{formData.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <PhoneIcon className="w-4 h-4" style={{ color: colors.primaryDark }} />
              <span className="text-sm" style={{ color: colors.textPrimary }}>{formData.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageIcon className="w-4 h-4" style={{ color: colors.primaryDark }} />
              <span className="text-sm" style={{ color: colors.textPrimary }}>{formData.reason}</span>
            </div>
          </div>
        </div>

        <div
          className="border rounded-lg p-4 mb-6"
          style={{ backgroundColor: `${colors.primaryLight}20`, borderColor: colors.primaryDark }}
        >
          <h4 className="font-medium mb-2" style={{ color: colors.primaryDark }}>What's Next?</h4>
          <ul className="text-sm text-left space-y-1" style={{ color: colors.primaryDark }}>
            <li>• Our counsellor will review your request</li>
            <li>• You'll receive a call/email within 24–48 hours</li>
            <li>• We'll schedule a convenient time for your session</li>
            <li>• Session details will be shared via email/SMS</li>
          </ul>
        </div>

        <button
          onClick={onReset}
          className="w-full text-white py-3 rounded-lg hover:opacity-90 transition-colors font-medium"
          style={{ backgroundColor: colors.primaryDark }}
        >
          Submit Another Request
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const CounsellingPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email:    '',
    phone:    '',
    reason:   ''
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error:   null
  });

  // Per-field validation errors returned by the API
  const [fieldErrors, setFieldErrors] = useState({});

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear the field error as the user types
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () =>
    formData.fullName.trim() &&
    formData.email.trim() &&
    formData.phone.trim() &&
    formData.reason;

  const handleSubmit = async () => {
    setFormStatus({ loading: true, success: false, error: null });
    setFieldErrors({});

    const result = await submitCounsellingRequest(formData);

    if (result.success) {
      setFormStatus({ loading: false, success: true, error: null });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setFormStatus({
        loading: false,
        success: false,
        error: result.message || 'Failed to submit mentorship request. Please try again.'
      });
      if (result.errors) {
        setFieldErrors(result.errors);
      }
    }
  };

  const handleReset = () => {
    setFormStatus({ loading: false, success: false, error: null });
    setFieldErrors({});
    setFormData({ fullName: '', email: '', phone: '', reason: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ---------------------------------------------------------------------------
  // Render success screen
  // ---------------------------------------------------------------------------
  if (formStatus.success) {
    return <SuccessView formData={formData} onReset={handleReset} />;
  }

  // ---------------------------------------------------------------------------
  // Shared input class helper
  // ---------------------------------------------------------------------------
  const inputClass = (field) =>
    `w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primaryLight focus:border-transparent transition-all font-medium ${
      fieldErrors[field]
        ? 'border-red-400 bg-red-50/40'
        : 'focus:border-[#134E4A]'
    }`;

  // ---------------------------------------------------------------------------
  // Render form
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="pt-20 sm:pt-24">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${colors.primaryLight}33` }}>
                <HeartIcon className="w-8 h-8" style={{ color: colors.primaryDark }} />
              </div>
              <div>
                <h1 className="text-3xl font-bold" style={{ color: colors.textPrimary }}>Mentorship</h1>
                <p className="text-lg" style={{ color: colors.textPrimary }}>
                  Get expert guidance for your preparation journey
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${colors.primaryLight}33` }}
            >
              <BookIcon className="w-8 h-8" style={{ color: colors.primaryDark }} />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: colors.textPrimary }}>
              Request Mentorship Session
            </h2>
            <p style={{ color: colors.textSecondary }}>
              Fill in your details and we'll contact you to schedule your session.
              A confirmation email will be sent automatically.
            </p>
          </div>

          {/* Global Error Banner */}
          {formStatus.error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-700 text-sm font-medium">{formStatus.error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Full Name */}
            <div>
              <label
                className="flex items-center gap-2 text-sm font-semibold mb-2"
                style={{ color: colors.textPrimary }}
              >
                <UserIcon className="w-4 h-4" />
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className={inputClass('fullName')}
                style={{ borderColor: fieldErrors.fullName ? undefined : colors.borderLight, color: colors.textPrimary }}
                placeholder="Enter your full name"
                disabled={formStatus.loading}
                required
              />
              {fieldErrors.fullName && (
                <p className="text-xs font-bold text-red-600 mt-1">{fieldErrors.fullName}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label
                className="flex items-center gap-2 text-sm font-semibold mb-2"
                style={{ color: colors.textPrimary }}
              >
                <MailIcon className="w-4 h-4" />
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={inputClass('email')}
                style={{ borderColor: fieldErrors.email ? undefined : colors.borderLight, color: colors.textPrimary }}
                placeholder="Enter your email address"
                disabled={formStatus.loading}
                required
              />
              {fieldErrors.email && (
                <p className="text-xs font-bold text-red-600 mt-1">{fieldErrors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label
                className="flex items-center gap-2 text-sm font-semibold mb-2"
                style={{ color: colors.textPrimary }}
              >
                <PhoneIcon className="w-4 h-4" />
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={inputClass('phone')}
                style={{ borderColor: fieldErrors.phone ? undefined : colors.borderLight, color: colors.textPrimary }}
                placeholder="Enter your phone number"
                disabled={formStatus.loading}
                required
              />
              {fieldErrors.phone && (
                <p className="text-xs font-bold text-red-600 mt-1">{fieldErrors.phone}</p>
              )}
            </div>

            {/* Reason */}
            <div>
              <label
                className="flex items-center gap-2 text-sm font-semibold mb-2"
                style={{ color: colors.textPrimary }}
              >
                <MessageIcon className="w-4 h-4" />
                Reason for Mentorship <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                className={inputClass('reason')}
                style={{ borderColor: fieldErrors.reason ? undefined : colors.borderLight, color: colors.textPrimary }}
                disabled={formStatus.loading}
                required
              >
                <option value="">Select a reason</option>
                {REASONS.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
              {fieldErrors.reason && (
                <p className="text-xs font-bold text-red-600 mt-1">{fieldErrors.reason}</p>
              )}
            </div>

            {/* Mentoring Slot Info Banner (unchanged content) */}
            <div
              className="border rounded-lg p-4"
              style={{
                borderColor:     colors.primaryLight,
                backgroundColor: `${colors.primaryLight}20`,
                borderRadius:    '0.5rem',
                padding:         '1rem'
              }}
            >
              <ul className="text-sm space-y-0.5" style={{ color: colors.primaryDark }}>
                <li className="font-bold">Have You Booked Your Free Mentoring Slot Yet?</li>
                <li>To Schedule Your Free Mentoring Session</li>
                <li className="font-semibold text-red-600">⚠ Attention: Limited Slots Available</li>
                <li>Dear Aspirants, many students are missing the chance</li>
                <li>to get personal mentoring due to limited availability.</li>
                <li>We request you to book your FREE one-on-one mentoring session</li>
                <li>today to avoid missing out.</li>
                <li>Our expert will reach out to you within 24 hrs.</li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!validateForm() || formStatus.loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 text-white rounded-lg hover:opacity-90 disabled:cursor-not-allowed transition-colors font-medium text-lg"
              style={{
                backgroundColor:
                  !validateForm() || formStatus.loading ? '#9CA3AF' : colors.primaryDark
              }}
            >
              {formStatus.loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Submitting Request...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5" />
                  <span>Submit Mentorship Request</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Additional Info Card (unchanged) */}
        <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-semibold mb-4" style={{ color: colors.primaryDark }}>
            What to Expect from Your Mentorship Session:
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2" style={{ color: colors.primaryLight }}>
                Personalized Guidance
              </h4>
              <p style={{ color: colors.textSecondary }}>
                Get customized study plans and strategies based on your current preparation level and goals.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2" style={{ color: colors.primaryLight }}>Expert Advice</h4>
              <p style={{ color: colors.textSecondary }}>
                Receive guidance from experienced mentors who understand the examination process.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2" style={{ color: colors.primaryLight }}>
                Motivation Support
              </h4>
              <p style={{ color: colors.textSecondary }}>
                Get the mental health support and motivation needed to stay focused on your journey.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2" style={{ color: colors.primaryLight }}>
                Flexible Scheduling
              </h4>
              <p style={{ color: colors.textSecondary }}>
                We'll work with your schedule to find the most convenient time for your session.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CounsellingPage;